import { useDraggable } from '@dnd-kit/core';
import { CameraGroupCamera } from '@hakimo-ui/hakimo/types';
import { DragIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button } from '@hakimo-ui/shared/ui-base';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import CameraPreviewModal from './CameraPreviewModal';

interface Props {
  cam: CameraGroupCamera;
  id: string;
  name: string;
  imageUrl?: string;
  livestreamUrl?: string;
  draggable?: boolean;
  showDragIcon?: boolean;
  showCancelButton?: boolean;
  onCancel?: (cam: CameraGroupCamera) => void;
  isDragging?: boolean;
  className?: string;
}

function DraggableCameraItem({
  cam,
  id,
  name,
  imageUrl,
  livestreamUrl,
  draggable = false,
  showDragIcon = false,
  showCancelButton = false,
  onCancel,
  isDragging = false,
  className = '',
}: Props) {
  const [showPreview, setShowPreview] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const { setNodeRef, listeners, attributes, transform } = useDraggable({
    id: cam.id,
    data: cam,
    disabled: !draggable,
  });

  return (
    <>
      <div
        ref={draggable ? setNodeRef : undefined}
        {...(draggable ? listeners : {})}
        {...(draggable ? attributes : {})}
        style={{
          transform: transform
            ? `translate(${transform.x}px, ${transform.y}px)`
            : undefined,
          opacity: isDragging ? 0 : 1,
        }}
        className={`hover:bg-onlight-bg-3/50 dark:hover:bg-ondark-bg-3/50 group flex items-center justify-between rounded px-2 py-3 text-sm ${
          draggable ? 'cursor-grab' : ''
        } ${className}`}
      >
        <span className="flex-1 truncate">{cam.name}</span>

        <div className="ml-auto flex items-center">
          <div className="border-onlight-line-2 dark:border-ondark-line-2 relative h-6 w-10 flex-shrink-0 overflow-hidden rounded-md border">
            {!imageLoaded && (
              <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-gray-300/50 to-transparent dark:via-white/10" />
            )}

            {imageUrl && (
              <img
                src={imageUrl}
                alt={name}
                onLoad={() => setImageLoaded(true)}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPreview(true);
                }}
                className="h-6 w-10 cursor-pointer rounded object-cover"
              />
            )}
          </div>

          {showCancelButton && onCancel && (
            <Button
              variant="icon"
              onClick={(e) => {
                e.stopPropagation();
                onCancel(cam);
              }}
              className="ml-2"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          )}

          {!showCancelButton && showDragIcon && (
            <DragIcon className="text-ondark-text-2 h-4 w-0 overflow-hidden opacity-0 transition-all duration-200 group-hover:ml-3 group-hover:w-4 group-hover:opacity-100" />
          )}
        </div>
      </div>

      <CameraPreviewModal
        id={id}
        name={name}
        imageUrl={imageUrl}
        livestreamUrl={livestreamUrl}
        open={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
}

export default DraggableCameraItem;
